import { useEffect, useState, useSyncExternalStore } from "react";
import type { TaskStatus } from "./types";

// Client-side tracking of pipeline jobs this browser started.
//
// A queued trigger returns a Celery task_id; <JobWatcher> polls
// GET /tasks/{task_id} until the task is ready, then records the outcome (with
// the run id the task produced) and refreshes the run lists. The API writes
// each run in one transaction, so the /runs endpoints show nothing until it
// finishes. The task state is the only live signal.

export type JobKind = "sync" | "extract" | "index" | "transcribe";

export interface Job {
  kind: JobKind;
  label: string;
  startedAt: number;
  // null while the trigger request is in flight, or for calls that run
  // inline and return their result directly.
  taskId: string | null;
  state: string; // "SUBMITTING" until a task id arrives, then the Celery state
}

export interface FinishedJob {
  kind: JobKind;
  label: string;
  taskId: string | null;
  // The run's own status on success ("success" | "partial" | "failed"), or
  // "failed" / "revoked" for the task, or "unknown" if we stopped waiting.
  status: string;
  runId: number | null;
  result: TaskStatus["result"];
  error: string | null;
  finishedAt: number;
}

// Celery reports PENDING for queued tasks AND for unknown/expired ids. With
// task_track_started on, a task a worker has picked up says STARTED, so a
// long PENDING means no worker took it (or the id is unknown).
export const PENDING_TIMEOUT_MS = 10 * 60 * 1000;
// Upper bound on a running job, so a lost task never polls forever.
export const JOB_TIMEOUT_MS = 60 * 60 * 1000;
export const TASK_POLL_MS = 2500;

let jobs: Job[] = [];
let finished: Partial<Record<JobKind, FinishedJob>> = {};
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function startJob(kind: JobKind, label: string, now = Date.now()) {
  jobs = [...jobs.filter((j) => j.kind !== kind), { kind, label, startedAt: now, taskId: null, state: "SUBMITTING" }];
  emit();
}

export function attachTask(kind: JobKind, taskId: string) {
  jobs = jobs.map((j) => (j.kind === kind ? { ...j, taskId, state: "PENDING" } : j));
  emit();
}

export function setTaskState(taskId: string, state: string) {
  if (!jobs.some((j) => j.taskId === taskId && j.state !== state)) return;
  jobs = jobs.map((j) => (j.taskId === taskId ? { ...j, state } : j));
  emit();
}

export function cancelJob(kind: JobKind) {
  if (!jobs.some((j) => j.kind === kind)) return;
  jobs = jobs.filter((j) => j.kind !== kind);
  emit();
}

// Ends the job(s) of `kinds` and records how they finished.
export function finishJobs(kinds: JobKind[], outcome: Omit<FinishedJob, "kind" | "label" | "finishedAt">, now = Date.now()) {
  const next = { ...finished };
  for (const kind of kinds) {
    const job = jobs.find((j) => j.kind === kind);
    next[kind] = { ...outcome, kind, label: job?.label ?? kind, finishedAt: now };
  }
  finished = next;
  jobs = jobs.filter((j) => !kinds.includes(j.kind));
  emit();
}

export function dismissFinished(kind: JobKind) {
  if (!finished[kind]) return;
  finished = { ...finished };
  delete finished[kind];
  emit();
}

export type TaskVerdict = null | Omit<FinishedJob, "kind" | "label" | "finishedAt" | "taskId">;

// Pure decision for one poll: null = keep polling, otherwise how the job ended.
export function evaluateTask(job: Pick<Job, "startedAt">, task: TaskStatus | undefined, now = Date.now()): TaskVerdict {
  const waited = now - job.startedAt;
  if (task?.ready) {
    if (task.state === "SUCCESS") {
      return {
        status: typeof task.result?.status === "string" ? task.result.status : "success",
        runId: typeof task.result?.run_id === "number" ? task.result.run_id : null,
        result: task.result,
        error: null,
      };
    }
    return {
      status: task.state === "REVOKED" ? "revoked" : "failed",
      runId: null,
      result: null,
      error: task.error ?? (task.state === "REVOKED" ? "The task was cancelled." : "The task failed."),
    };
  }
  const neverStarted = !task || task.state === "PENDING";
  if ((neverStarted && waited > PENDING_TIMEOUT_MS) || waited > JOB_TIMEOUT_MS) {
    return {
      status: "unknown",
      runId: null,
      result: null,
      error: neverStarted
        ? "No worker picked this job up (or its task id expired). Check the run list and the Celery workers on the dashboard."
        : "Stopped waiting for this job. Check the run list for its outcome.",
    };
  }
  return null;
}

export function jobStateLabel(job: Pick<Job, "state">) {
  if (job.state === "SUBMITTING") return "starting";
  if (job.state === "PENDING") return "queued";
  return "running";
}

export function getJob(kind: JobKind) {
  return jobs.find((j) => j.kind === kind);
}

export function resetJobs() {
  jobs = [];
  finished = {};
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useJob(kind: JobKind) {
  return useSyncExternalStore(subscribe, () => getJob(kind));
}

export function useJobs() {
  return useSyncExternalStore(subscribe, () => jobs);
}

export function useFinishedJob(kind: JobKind) {
  return useSyncExternalStore(subscribe, () => finished[kind]);
}

// Re-renders every second while `active`, for live elapsed timers.
export function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}
